import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Autocomplete, Button, CircularProgress, TextField, Switch, FormControlLabel, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { FiFileText, FiRefreshCw } from "react-icons/fi";
import { IoPrintOutline } from "react-icons/io5";
import authHeader from "../../Auth/authHeader";
import LoginUserDetailed from "../../Auth/LoginUserDetailed";
import { FiscalYearContext } from "../../FiscalYear/FiscalYearContext";
import CrudPageHeader from "../../Components/CrudPageHeader";
import { getApiErrorMessage, handleSuccess, errorAlert } from "../../Components/Alerts";
import { defaultFiscalDate } from "./cashReconciliationModel";
import { compareReported, money, validateSalesPeriod } from "./salesReconciliationModel";
import { loadSalesPreview, loadSalesSellers, loadTransferOptions, saveReconciliationTransfer, loadReconciliationCompany } from "./salesReconciliationApi";
import ReconciliationPrint from "./ReconciliationPrint";
import "../../Style/TableStyle.scss";
import "../ReceivablesCRUD/ReceivablesPage.scss";
import "./SalesReconciliationPage.scss";

const euros = (value) => `${money(value)} €`;

export default function SalesReconciliationPage() {
  const { fiscalYear } = useContext(FiscalYearContext);
  const headers = useMemo(() => authHeader(), []);
  const adminEmail = LoginUserDetailed();
  const [dates, setDates] = useState(() => ({ from: defaultFiscalDate(fiscalYear), to: defaultFiscalDate(fiscalYear), year: fiscalYear }));
  const [sellers, setSellers] = useState([]);
  const [seller, setSeller] = useState(null);
  const [sellersLoading, setSellersLoading] = useState(false);
  const [sellersError, setSellersError] = useState("");
  const [reload, setReload] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [openingCash, setOpeningCash] = useState("");
  const [returnedCash, setReturnedCash] = useState("");
  const [keepOpeningCash, setKeepOpeningCash] = useState(true);
  const [transferOptions, setTransferOptions] = useState(null);
  const [destination, setDestination] = useState(null);
  const [transferError, setTransferError] = useState("");
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedTransfer, setSavedTransfer] = useState(null);
  const [confirmTransfer, setConfirmTransfer] = useState(false);
  const [printReceipt, setPrintReceipt] = useState(null);
  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companyError, setCompanyError] = useState("");
  const [companyReload, setCompanyReload] = useState(0);
  const [printing, setPrinting] = useState(false);
  const requestRef = useRef(null);

  const clear = () => {
    requestRef.current?.abort(); requestRef.current = null;
    setLoading(false); setError(""); setPreview(null); setOpeningCash(""); setReturnedCash(""); setKeepOpeningCash(true);
    setTransferOptions(null); setDestination(null); setTransferError(""); setSavedTransfer(null); setConfirmTransfer(false); setOptionsLoading(false);
    setPrintReceipt(null);
  };

  useEffect(() => {
    const controller = new AbortController();
    setCompanyLoading(true); setCompanyError("");
    loadReconciliationCompany(headers,controller.signal)
      .then((details) => { if (!controller.signal.aborted) setCompany(details); })
      .catch((err) => { if (!controller.signal.aborted) setCompanyError(getApiErrorMessage(err)); })
      .finally(() => { if (!controller.signal.aborted) setCompanyLoading(false); });
    return () => controller.abort();
  }, [headers,companyReload]);

  useEffect(() => {
    requestRef.current?.abort(); requestRef.current = null;
    setLoading(false); setError(""); setPreview(null); setOpeningCash(""); setReturnedCash(""); setKeepOpeningCash(true);
    setTransferOptions(null); setDestination(null); setTransferError(""); setSavedTransfer(null); setConfirmTransfer(false); setOptionsLoading(false);
    setPrintReceipt(null);
    setSeller(null); setSellers([]);
    setDates({ from: defaultFiscalDate(fiscalYear), to: defaultFiscalDate(fiscalYear), year: fiscalYear });
  }, [fiscalYear]);

  useEffect(() => {
    if (dates.year !== fiscalYear) return;
    const controller = new AbortController();
    const validation = validateSalesPeriod(dates.from, dates.to, fiscalYear);
    setSellers([]); setSeller(null); setSellersError(validation);
    if (validation) { setSellersLoading(false); return; }
    setSellersLoading(true);
    loadSalesSellers(headers, dates.from, dates.to, controller.signal)
      .then((items) => { if (!controller.signal.aborted) setSellers(items); })
      .catch((err) => { if (!controller.signal.aborted) setSellersError(getApiErrorMessage(err)); })
      .finally(() => { if (!controller.signal.aborted) setSellersLoading(false); });
    return () => controller.abort();
  }, [dates, fiscalYear, headers, reload]);

  useEffect(() => () => requestRef.current?.abort(), []);

  const fetchPreview = async (event) => {
    event.preventDefault();
    const validation = validateSalesPeriod(dates.from, dates.to, fiscalYear);
    if (validation || !seller) { setError(validation || "Zgjidhni shitësin."); return; }
    clear();
    const controller = new AbortController(); requestRef.current = controller;
    setLoading(true);
    try {
      const result = await loadSalesPreview(headers, seller, dates.from, dates.to, controller.signal);
      if (!controller.signal.aborted) {
        setPreview(result); setOptionsLoading(true);
        try {
          const options = await loadTransferOptions(headers, seller, dates.from, dates.to, controller.signal);
          if (!controller.signal.aborted) setTransferOptions(options);
        } catch (err) { if (!controller.signal.aborted) setTransferError(getApiErrorMessage(err)); }
        finally { if (!controller.signal.aborted) setOptionsLoading(false); }
      }
    } catch (err) { if (!controller.signal.aborted) setError(getApiErrorMessage(err)); }
    finally { if (!controller.signal.aborted) setLoading(false); }
  };

  const initialCash = openingCash === "" ? null : Number(openingCash);
  const validInitial = initialCash !== null && Number.isFinite(initialCash) && initialCash >= 0;
  const cashDue = preview && validInitial
    ? Math.round((preview.Cash + (keepOpeningCash ? 0 : initialCash)) * 100) / 100 : null;
  const difference = cashDue === null ? null : compareReported(cashDue, returnedCash);
  const couponCount = preview?.Documents.filter((document) => document.ActionType === "POS").length || 0;
  const moneyInputProps = { startAdornment: <InputAdornment position="start">€</InputAdornment> };
  const canTransfer = preview?.DocumentCount > 0 && transferOptions && destination && validInitial && Number(returnedCash) > 0 &&
    Number.isFinite(Number(returnedCash)) && !saving && !savedTransfer;
  const printSavedReceipt = async () => {
    const root = document.querySelector(".SRP__print");
    if (!root || printing) return;
    setPrinting(true);
    try {
      await Promise.all(Array.from(root.querySelectorAll("img")).map((img) => img.decode?.().catch(() => {})));
      if (root.isConnected) window.print();
    } finally { setPrinting(false); }
  };
  const submitTransfer = async () => {
    if (!canTransfer) return;
    setSaving(true); setTransferError("");
    const selection = requestRef.current;
    try {
      const result = await saveReconciliationTransfer(headers, {
        Seller: preview.Seller, FromDate: dates.from, ToDate: dates.to,
        ExpectedSourceAccountID: transferOptions.Source.AccountID, DestinationAccountID: destination.AccountID, Amount: Number(returnedCash),
        OpeningCash: Number(openingCash), KeepOpeningCash: keepOpeningCash,
        ExpectedCash: preview.Cash, ExpectedNetTotal: preview.NetTotal, ExpectedDocumentCount: preview.DocumentCount,
        ExpectedLastTransactionID: preview.LastTransactionID ?? Math.max(...preview.Documents.map((document) => document.TransactionID)),
      });
      if (!selection?.signal.aborted) {
        setSavedTransfer(result); setConfirmTransfer(false);
        setPrintReceipt({
          transfer: { ...result }, seller: preview.Seller, admin: adminEmail,
          from: dates.from, to: dates.to, year: dates.year,
          source: { ...transferOptions.Source }, destination: { ...destination },
          couponCount, total: preview.NetTotal, cash: preview.Cash, bank: preview.Bank, other: preview.Other,
          opening: initialCash, keepOpening: keepOpeningCash, due: cashDue,
          difference: Math.round((result.Amount - cashDue) * 100) / 100,
        });
        setSellers((items) => items.filter((item) => item !== preview.Seller));
        setSeller(null); setReload((value) => value + 1);
      }
      handleSuccess(`Transferi u ruajt: ${euros(result.Amount)}.`);
    } catch (err) {
      if (!selection?.signal.aborted) setTransferError(getApiErrorMessage(err));
      else errorAlert(err);
    }
    finally { setSaving(false); }
  };
  return <div className="Crud__Container REC__page SRP">
    <div className="shfaqePermbajtjen REC__content SRP__content">
      <CrudPageHeader icon={FiFileText} title="Barazimi i arkave"
        description="Përmbledhja e shitjeve dhe dorëzimi i parave."
        className="REC__header"
        secondaryAction={<>
          <Button startIcon={<IoPrintOutline />} disabled={!printReceipt || saving || printing || companyLoading || !company || Boolean(companyError)}
            onClick={printSavedReceipt} sx={{ textTransform: "none" }}>Printo barazimin</Button>
          <Button startIcon={<FiRefreshCw />} disabled={saving || loading || sellersLoading}
            onClick={() => { clear(); setReload((value) => value + 1); }} sx={{ textTransform: "none" }}>Rifresko listën</Button>
          <span className="SRP__year">Viti {fiscalYear}</span>
        </>} />
      <div className="SRP__body">
    <section className="SRP__selection">
      <form className="SRP__filters" onSubmit={fetchPreview} noValidate>
        <Autocomplete options={sellers} value={seller} loading={sellersLoading}
          disabled={saving || sellersLoading || Boolean(sellersError)}
          onChange={(_, next) => { clear(); setSeller(next); }}
          noOptionsText="Nuk ka shitës për këtë periudhë" loadingText="Duke ngarkuar..."
          renderInput={(params) => <TextField {...params} label="Shitësi / Kamarieri" />} />
        <TextField label="Nga data" type="date" value={dates.from} disabled={saving} InputLabelProps={{ shrink: true }}
          inputProps={{ min: `${fiscalYear}-01-01`, max: `${fiscalYear}-12-31` }}
          onChange={(event) => { clear(); setSeller(null); setDates({ ...dates, from: event.target.value }); }} />
        <TextField label="Deri më" type="date" value={dates.to} disabled={saving} InputLabelProps={{ shrink: true }}
          inputProps={{ min: dates.from, max: `${fiscalYear}-12-31` }}
          onChange={(event) => { clear(); setSeller(null); setDates({ ...dates, to: event.target.value }); }} />
        <Button type="submit" variant="contained" disabled={saving || loading || sellersLoading || !seller || Boolean(sellersError)}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
          {loading ? "Duke ngarkuar..." : "Shfaq përmbledhjen"}
        </Button>
      </form>
      {sellersError && <Alert severity="error" action={<Button onClick={() => setReload((value) => value + 1)}>Riprovo</Button>}>{sellersError}</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
    </section>
    {!preview && !loading && <div className="SRP__empty"><FiFileText /><h2>Zgjidh shitësin dhe periudhën</h2><p>Përmbledhja e shitjeve do të shfaqet këtu.</p></div>}
    {preview && <>
      <div className="SRP__workspace">
        <section className="SRP__sales" aria-labelledby="sales-heading">
          <div className="SRP__panelHeader"><h2 id="sales-heading">Shitjet e pabarazuara</h2><span className="SRP__couponBadge"><strong data-testid="coupon-count">{couponCount}</strong> kupona të ardhur</span></div>
          <p className="SRP__seller">{preview.Seller}</p>
          <div className="SRP__total"><span>Totali komplet</span><strong>{euros(preview.NetTotal)}</strong></div>
          <dl className="SRP__breakdown">
            <div><dt><span className="SRP__dot SRP__dot--cash" />Cash nga shitjet</dt><dd>{euros(preview.Cash)}</dd></div>
            <div><dt><span className="SRP__dot SRP__dot--card" />Kartelë / Bankë</dt><dd>{euros(preview.Bank)}</dd></div>
            <div><dt><span className="SRP__dot SRP__dot--other" />Të tjera</dt><dd>{euros(preview.Other)}</dd></div>
          </dl>
        </section>
        <section className="SRP__settlement" aria-labelledby="settlement-heading">
          <div className="SRP__panelHeader"><h2 id="settlement-heading">Dorëzimi i parave</h2></div>
          <div className="SRP__initial">
            <TextField label="Cash fillestar" type="number" value={openingCash} placeholder="0.00" fullWidth disabled={saving || Boolean(savedTransfer)}
              InputProps={moneyInputProps} inputProps={{ min: 0, step: "0.01" }}
              error={openingCash !== "" && !validInitial}
              onChange={(event) => setOpeningCash(event.target.value)} />
            <FormControlLabel control={<Switch checked={keepOpeningCash} disabled={saving || Boolean(savedTransfer)} onChange={(event) => setKeepOpeningCash(event.target.checked)} />} label="Cash fillestar mbetet në arkë" />
          </div>
          <div className="SRP__due"><span>Duhet të dorëzojë</span><strong data-testid="cash-due">{cashDue === null ? "—" : euros(cashDue)}</strong></div>
          <TextField label="Sa para u dorëzuan?" type="number" value={returnedCash} fullWidth disabled={cashDue === null || saving || Boolean(savedTransfer)}
            InputProps={moneyInputProps} inputProps={{ min: 0, step: "0.01" }}
            error={returnedCash !== "" && Number(returnedCash) < 0}
            onChange={(event) => setReturnedCash(event.target.value)} />
          {difference !== null && Number(returnedCash) >= 0 && <div className={`SRP__difference SRP__difference--${difference === 0 ? "match" : difference < 0 ? "short" : "over"}`} role="status">
            <span>{difference === 0 ? "Shuma përputhet" : difference < 0 ? "Mungesë" : "Tepricë"}</span>
            <strong>{euros(Math.abs(difference))}</strong>
          </div>}
          <div className="SRP__transfer">
            {transferOptions && <div className="SRP__source"><span>Nga arka e shitësit</span><strong>{transferOptions.Source.AccountName}</strong></div>}
            <Autocomplete options={transferOptions?.Destinations || []} value={destination} loading={optionsLoading}
              disabled={saving || Boolean(savedTransfer) || !transferOptions}
              getOptionLabel={(option) => `${option.AccountName} (${option.LedgerID})`}
              isOptionEqualToValue={(option, value) => option.AccountID === value.AccountID}
              onChange={(_, next) => setDestination(next)} noOptionsText="Nuk ka llogari destinacioni"
              renderInput={(params) => <TextField {...params} label="Transfero në" />} />
            <Button fullWidth variant="contained" disabled={!canTransfer} onClick={() => setConfirmTransfer(true)}>
              {saving ? "Duke ruajtur..." : "Ruaj dorëzimin dhe transfero"}
            </Button>
            {transferError && <Alert severity="error">{transferError}</Alert>}
            {savedTransfer && <Alert severity="success">Transferi TRS-{dates.year}-{savedTransfer.InvoiceNr} u ruajt: {euros(savedTransfer.Amount)}.</Alert>}
            {savedTransfer && companyError && <Alert severity="warning"
              action={<Button onClick={() => setCompanyReload((value) => value + 1)}>Riprovo</Button>}>{companyError}</Alert>}
          </div>
        </section>
      </div>
      {(preview.DocumentsWithPaymentDifference > 0 || preview.DocumentsWithLineDifference > 0) &&
        <Alert severity="warning">Ka mospërputhje në pagesat ose linjat e shitjeve. Kontrolloji para dorëzimit.</Alert>}
      {preview.DocumentCount === 0 && <Alert severity="info">Nuk ka shitje për këtë periudhë.</Alert>}
    </>}
      <Dialog open={confirmTransfer} onClose={() => { if (!saving) setConfirmTransfer(false); }} fullWidth maxWidth="xs">
        <DialogTitle>Konfirmo transferin</DialogTitle>
        <DialogContent>
          <p>{transferOptions?.Source.AccountName} → {destination?.AccountName}</p>
          <strong>{euros(Number(returnedCash || 0))}</strong>
          {difference !== null && difference !== 0 && <Alert severity="warning">{difference < 0 ? "Mungesë" : "Tepricë"}: {euros(Math.abs(difference))}. Transferohet shuma e dorëzuar.</Alert>}
          {transferError && <Alert severity="error">{transferError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setConfirmTransfer(false)}>Anulo</Button>
          <Button variant="contained" disabled={!canTransfer} onClick={submitTransfer}>{saving ? "Duke ruajtur..." : "Konfirmo"}</Button>
        </DialogActions>
      </Dialog>
      </div>
    </div>
    <ReconciliationPrint receipt={printReceipt} company={company} />
  </div>;
}
